import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import KeywordsInput from 'src/components/Inputs/KeywordsInput';
import { ImagePreviewTooltip } from './ImagePreviewTooltip';
import { TintedTextField } from '../TintedTextField';
import { UseCaseSelector } from 'src/components/UseCaseSelector/UseCaseSelector';
import { SubUseCaseSelector } from 'src/components/UseCaseSelector/SubUseCaseSelector';
import React from 'react';
import { CreateCampaignParams, UseCasesResponse } from 'src/api/campaignRegistry';
import { DefaultCustomField } from 'src/constants/types';
import {
  CampaignFormState,
  CampaignInfoTabViewModel,
} from 'src/modules/companies/types';
import {
  MAX_SAMPLE_MESSAGES,
  SAMPLE_MESSAGE_FIELD_MAX_ROWS,
  devOnlyFieldSx,
  getExistingSampleMessages,
} from 'src/modules/companies/helpers';
import {
  StatusChips,
  ScrollContainer,
  LoadingSpinner,
  TemplateTextFieldWrapper,
} from '../shared/components';

type SubscriberOptionGroupProps = {
  title: string;
  type: 'help' | 'optin' | 'optout';
  checked: boolean;
  message: string;
  keywords: string[];
  newKeyword: string | undefined;
  setNewKeyword: (value: string) => void;
  messageLabel: string;
  keywordLabel: string;
  onCheckboxChange: (checked: boolean) => void;
  onMessageChange: (value: string) => void;
  onKeywordAdd: (keywordValue: string) => void;
  onKeywordRemove: (keywordToRemove: string) => void;
  isReadOnly: boolean;
  messageError?: string;
  customFields?: DefaultCustomField[];
};

const SubscriberOptionGroup: React.FC<SubscriberOptionGroupProps> = ({
  title,
  type,
  checked,
  message,
  keywords,
  newKeyword,
  setNewKeyword,
  messageLabel,
  keywordLabel,
  onCheckboxChange,
  onMessageChange,
  onKeywordAdd,
  onKeywordRemove,
  isReadOnly,
  messageError,
  customFields,
}) => (
    <Grid item xs={12}>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={e => onCheckboxChange(e.target.checked)}
              disabled={isReadOnly}
            />
          }
          label={<Typography variant="h6">{title}</Typography>}
        />
        {checked && (
          <Box ml={4} mt={1.25}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {customFields ? (
                  <TemplateTextFieldWrapper
                    label={messageLabel}
                    value={message}
                    onChange={onMessageChange}
                    disabled={isReadOnly}
                    error={!!messageError}
                    errorText={messageError}
                    customFields={customFields}
                    multiline
                    minHeight="56px"
                    fullWidth
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={SAMPLE_MESSAGE_FIELD_MAX_ROWS}
                    label={messageLabel}
                    value={message}
                    onChange={e => onMessageChange(e.target.value)}
                    disabled={isReadOnly}
                    error={!!messageError}
                  />
                )}
                {!customFields && messageError && (
                  <FormHelperText error={!!messageError}>
                    {messageError}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <KeywordsInput
                  label={keywordLabel}
                  keywordType={
                    `${type}Keywords` as 'helpKeywords' | 'optinKeywords' | 'optoutKeywords'
                  }
                  keywords={keywords}
                  newKeyword={newKeyword || ''}
                  setNewKeyword={setNewKeyword}
                  onAddKeyword={(_, keywordValue) => onKeywordAdd(keywordValue)}
                  onRemoveKeyword={(_, keywordToRemove) => onKeywordRemove(keywordToRemove)}
                  isReadOnly={isReadOnly}
                />
              </Grid>
            </Grid>
          </Box>
      )}
    </Box>
    </Grid>
  );

type SampleMessagesSectionProps = {
  formValues: CampaignFormState;
  isExistingCampaign: boolean;
  isReadOnly: boolean;
  visibleSampleMessages: number[];
  onSampleChange: (field: keyof CreateCampaignParams, value: string | string[] | boolean) => void;
  onAddSampleMessage: () => void;
};

const SampleMessageField: React.FC<{
  num: number;
  formValues: CampaignFormState;
  onSampleChange: (field: keyof CreateCampaignParams, value: string | string[] | boolean) => void;
  isReadOnly: boolean;
}> = ({ num, formValues, onSampleChange, isReadOnly }) => {
  const sampleKey = `sample${num}` as keyof CreateCampaignParams;
  const sampleValue = (formValues[sampleKey] as string) || '';

  return (
    <Grid item xs={12} key={num}>
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={SAMPLE_MESSAGE_FIELD_MAX_ROWS}
        label={`Sample Message ${num}`}
        value={sampleValue}
        onChange={e => onSampleChange(sampleKey, e.target.value)}
        disabled={isReadOnly}
      />
    </Grid>
  );
};

const SampleMessagesSection: React.FC<SampleMessagesSectionProps> = ({
  formValues,
  isExistingCampaign,
  isReadOnly,
  visibleSampleMessages,
  onSampleChange,
  onAddSampleMessage,
}) => {
  const sampleNumbers = isExistingCampaign
    ? getExistingSampleMessages(formValues)
    : visibleSampleMessages;

  return (
    <>
      {sampleNumbers.map(num => (
        <SampleMessageField
          key={num}
          num={num}
          formValues={formValues}
          onSampleChange={onSampleChange}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isExistingCampaign && visibleSampleMessages.length < MAX_SAMPLE_MESSAGES && (
        <Grid item xs={12}>
          <Button variant="outlined" onClick={onAddSampleMessage} sx={{ mt: 0 }}>
            Add Sample Message {visibleSampleMessages.length + 1}
          </Button>
        </Grid>
      )}
    </>
  );
};

const CampaignUseCaseSelector: React.FC<{
  formValues: CampaignFormState;
  updateField: (field: keyof CampaignFormState, value: string | string[] | boolean | undefined) => void;
  isReadOnly: boolean;
  error?: string;
  useCasesData: UseCasesResponse;
}> = ({ formValues, updateField, isReadOnly, error, useCasesData }) => {
  return (
    <UseCaseSelector
      value={formValues.usecase}
      onChange={(value) => updateField('usecase', value)}
      error={error}
      disabled={isReadOnly}
      useCasesData={useCasesData}
    />
  );
};

const CampaignSubUseCaseSelector: React.FC<{
  formValues: CampaignFormState;
  updateField: (field: keyof CampaignFormState, value: string | string[] | boolean | undefined) => void;
  isReadOnly: boolean;
  useCasesData: UseCasesResponse;
}> = ({ formValues, updateField, isReadOnly, useCasesData }) => {
  const handleSubUseCasesChange = (value: string[]) => {
    updateField('subUsecases', value);
  };

  return (
    <SubUseCaseSelector
      useCaseValue={formValues.usecase}
      subUseCasesValue={formValues.subUsecases}
      onSubUseCasesChange={handleSubUseCasesChange}
      disabled={isReadOnly}
      useCasesData={useCasesData}
    />
  );
};

const CampaignInfoTabView = (props: CampaignInfoTabViewModel) => {
  const {
    isLoading,
    saving,
    isReadOnly,
    companyTcrCampaignId,
    existingCampaign,
    useCasesData,
    customFields,
    formValues,
    fieldErrors,
    phoneError,
    emailError,
    visibleSampleMessages,
    newOptinKeyword,
    newOptoutKeyword,
    newHelpKeyword,
    setNewOptinKeyword,
    setNewOptoutKeyword,
    setNewHelpKeyword,
    updateField,
    onInputChange,
    onAddKeyword,
    onRemoveKeyword,
    onAddSampleMessage,
    onRegister,
    onSaveInfo,
    isFormValid,
  } = props;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box mt={2.5}>
      <ScrollContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TintedTextField
              fullWidth
              tint={
                existingCampaign
                  ? formValues.status === 'ACTIVE'
                    ? 'success'
                    : 'warning'
                  : companyTcrCampaignId
                  ? 'success'
                  : 'none'
              }
              label="TCR Campaign ID"
              value={formValues.campaignId}
              InputLabelProps={{ shrink: formValues.campaignId ? true : false }}
              InputProps={{
                readOnly: true,
                endAdornment: <StatusChips status={formValues.status} />,
              }}
              placeholder={
                existingCampaign
                  ? 'Loaded from existing campaign'
                  : 'Will be generated after successful registration'
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="brandId"
              label="Brand ID"
              value={formValues.brandId}
              onChange={e => onInputChange('brandId', e.target.value)}
              disabled={isReadOnly}
              error={!!fieldErrors.brandId}
              helperText={fieldErrors.brandId || ''}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Reseller ID"
              value={formValues.resellerId}
              onChange={e => onInputChange('resellerId', e.target.value)}
              disabled={isReadOnly}
              sx={{ mt: 1.375 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CampaignUseCaseSelector
              formValues={formValues}
              updateField={updateField}
              isReadOnly={isReadOnly}
              error={fieldErrors.usecase}
              useCasesData={useCasesData}
            />
          </Grid>

          <Grid item xs={12}>
            <CampaignSubUseCaseSelector
              formValues={formValues}
              updateField={updateField}
              isReadOnly={isReadOnly}
              useCasesData={useCasesData}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="flyerLink"
              label="Flyer Link"
              value={formValues.flyerLink || ''}
              onChange={e => onInputChange('flyerLink', e.target.value)}
              disabled={isReadOnly}
              error={!!fieldErrors.flyerLink}
              helperText={fieldErrors.flyerLink || ''}
              InputProps={{
                endAdornment: formValues.flyerLink && formValues.flyerLink.trim() !== '' ? (
                  <ImagePreviewTooltip src={formValues.flyerLink} alt="Flyer Preview" dimmed={isReadOnly}>
                    ℹ️
                  </ImagePreviewTooltip>
                ) : undefined,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TemplateTextFieldWrapper
              id="privacyPolicyLink"
              label="Privacy Policy Link"
              value={formValues.privacyPolicyLink || ''}
              onChange={value => onInputChange('privacyPolicyLink', value)}
              disabled={isReadOnly}
              error={!!fieldErrors.privacyPolicyLink}
              errorText={fieldErrors.privacyPolicyLink || ''}
              customFields={customFields}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TemplateTextFieldWrapper
              id="termsAndConditionsLink"
              label="Terms and Conditions Link"
              value={formValues.termsAndConditionsLink || ''}
              onChange={value => onInputChange('termsAndConditionsLink', value)}
              disabled={isReadOnly}
              error={!!fieldErrors.termsAndConditionsLink}
              errorText={fieldErrors.termsAndConditionsLink || ''}
              customFields={customFields}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TemplateTextFieldWrapper
              id="description"
              label="Description"
              value={formValues.description}
              onChange={value => onInputChange('description', value)}
              disabled={isReadOnly}
              error={!!fieldErrors.description}
              errorText={fieldErrors.description || ''}
              customFields={customFields}
              multiline
              minHeight="150px"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TemplateTextFieldWrapper
              id="messageFlow"
              label="Message Flow"
              value={formValues.messageFlow || ''}
              onChange={value => onInputChange('messageFlow', value)}
              disabled={isReadOnly}
              error={!!(fieldErrors.messageFlow || phoneError)}
              errorText={fieldErrors.messageFlow || phoneError || ''}
              customFields={customFields}
              multiline
              minHeight="150px"
              fullWidth
            />
          </Grid>

          <SampleMessagesSection
            formValues={formValues}
            isExistingCampaign={!!existingCampaign}
            isReadOnly={isReadOnly}
            visibleSampleMessages={visibleSampleMessages}
            onSampleChange={onInputChange}
            onAddSampleMessage={onAddSampleMessage}
          />

          <SubscriberOptionGroup
            title="Subscriber Opt-in"
            type="optin"
            checked={formValues.subscriberOptin || false}
            message={formValues.optinMessage || ''}
            keywords={formValues.optinKeywords || []}
            newKeyword={newOptinKeyword}
            setNewKeyword={setNewOptinKeyword}
            messageLabel="Opt-in Message"
            keywordLabel="Opt-in Keywords"
            onCheckboxChange={checked => onInputChange('subscriberOptin', checked)}
            onMessageChange={value => onInputChange('optinMessage', value)}
            onKeywordAdd={keywordValue =>
              onAddKeyword('optinKeywords', keywordValue, setNewOptinKeyword)
            }
            onKeywordRemove={keywordToRemove =>
              onRemoveKeyword('optinKeywords', keywordToRemove)
            }
            isReadOnly={isReadOnly}
          />

          <SubscriberOptionGroup
            title="Subscriber Opt-out"
            type="optout"
            checked={formValues.subscriberOptout || false}
            message={formValues.optoutMessage || ''}
            keywords={formValues.optoutKeywords || []}
            newKeyword={newOptoutKeyword}
            setNewKeyword={setNewOptoutKeyword}
            messageLabel="Opt-out Message"
            keywordLabel="Opt-out Keywords"
            onCheckboxChange={checked => onInputChange('subscriberOptout', checked)}
            onMessageChange={value => onInputChange('optoutMessage', value)}
            onKeywordAdd={keywordValue =>
              onAddKeyword('optoutKeywords', keywordValue, setNewOptoutKeyword)
            }
            onKeywordRemove={keywordToRemove =>
              onRemoveKeyword('optoutKeywords', keywordToRemove)
            }
            isReadOnly={isReadOnly}
          />

          <SubscriberOptionGroup
            title="Subscriber Help"
            type="help"
            checked={formValues.subscriberHelp || false}
            message={formValues.helpMessage}
            keywords={formValues.helpKeywords || []}
            newKeyword={newHelpKeyword}
            setNewKeyword={setNewHelpKeyword}
            messageLabel="Help Message"
            keywordLabel="Help Keywords"
            onCheckboxChange={checked => onInputChange('subscriberHelp', checked)}
            onMessageChange={value => onInputChange('helpMessage', value)}
            onKeywordAdd={keywordValue =>
              onAddKeyword('helpKeywords', keywordValue, setNewHelpKeyword)
            }
            onKeywordRemove={keywordToRemove =>
              onRemoveKeyword('helpKeywords', keywordToRemove)
            }
            isReadOnly={isReadOnly}
            messageError={fieldErrors.helpMessage || emailError}
            customFields={customFields}
          />

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.mock}
                  onChange={e => onInputChange('mock', e.target.checked)}
                  color="primary"
                  disabled={isReadOnly}
                />
              }
              label="Mock Registration (Development Mode)"
              sx={devOnlyFieldSx}
            />
          </Grid>

          {!isReadOnly && (
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3.75}>
                <Button
                  variant="outlined"
                  onClick={onSaveInfo}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                  {saving ? 'Saving...' : 'Save Info'}
                </Button>
                <Button
                  variant="contained"
                  onClick={onRegister}
                  disabled={!isFormValid() || saving}
                  startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                  {saving ? 'Registering...' : 'Register Campaign'}
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
      </ScrollContainer>
    </Box>
  );
};

export default CampaignInfoTabView;
